import { RiskRewardType } from '../enums/enums';
import type BasicCombination from './basic-combination'; // type-only: avoids a runtime circular import

/** Reg-T style approximation of naked-option margin: 20% of the underlying. */
export const NAKED_MARGIN_RATE = 0.2;

export interface IReturnBasisInput {
  tradeCost: number; // < 0 means credit
  cost: number; // denominator kept for debit trades
  maxRisk: number | RiskRewardType; // combination.maxRisk(): a number <= 0, or UNLIMITED
  underlyingPrice: number; // price at entry (trades) or current (Portfolio); never the slider price
  multiplier: number; // combination.multiplier()
  uncoveredShortCalls: number; // uncoveredShortCalls(combination)
}

export const returnBasis = (input: IReturnBasisInput): number => {
  if (!(input.tradeCost < 0)) {
    return input.cost;
  }
  if (input.maxRisk === RiskRewardType.UNLIMITED) {
    return NAKED_MARGIN_RATE * input.underlyingPrice * input.multiplier * input.uncoveredShortCalls;
  }
  return Math.abs(input.maxRisk as number);
};

export const uncoveredShortCalls = (combination: BasicCombination): number => {
  const multiplier = combination.multiplier();
  let netCalls = 0;
  combination.positions.forEach((position) => {
    if (position.isCallType()) {
      netCalls += position.quantity;
    } else if (position.isSecurityType()) {
      netCalls += position.quantity / multiplier;
    }
  });
  return Math.max(1, Math.ceil(-netCalls));
};

/** Single entry point. useCostBasis = true for Portfolio. */
export const returnBasisFor = (
  combination: BasicCombination,
  useCostBasis: boolean,
  underlyingPrice: number,
): number => {
  const tradeCost = useCostBasis ? combination.costBasisTotal() : combination.costWithoutOwned();
  const debitBasis = useCostBasis ? combination.costBasisTotal() : combination.cost();
  if (combination.hasOnlyStx()) {
    return Math.abs(debitBasis); // stock only: return on trade value, long or short
  }
  if (!(tradeCost < 0)) {
    return debitBasis; // debit: skip maxRisk() entirely
  }
  return returnBasis({
    tradeCost,
    cost: debitBasis,
    maxRisk: combination.maxRisk(),
    underlyingPrice,
    multiplier: combination.multiplier(),
    uncoveredShortCalls: uncoveredShortCalls(combination),
  });
};
